import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { motion, AnimatePresence } from "framer-motion";
import { TableSkeleton } from "../components/skeletons";
import { EmptyState } from "../components/EmptyState";
import {
  Upload, Search, FileImage, FileText, FileCode, Shield, CheckCircle2,
  XCircle, Clock, Eye, Trash2, Plus, Link2, Download
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from "../components/ui/dialog";
import { useOrg } from "../contexts/OrgProvider";
import { FRAMEWORKS as ALL_FRAMEWORKS, FW_SHORT } from "../constants/frameworks";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter
} from "../components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";

const Evidence = () => {
  const { token } = useAuth();
  const { t } = useI18n();
  const [evidenceList, setEvidenceList] = useState([]);
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [showAddEvidence, setShowAddEvidence] = useState(false);
  const [uploading, setUploading] = useState(false);

  const { activeFrameworks } = useOrg();
  const orgFrameworks = ALL_FRAMEWORKS.filter((f) => activeFrameworks.includes(f.id));

  const [newEvidence, setNewEvidence] = useState({
    title: "",
    evidence_type: "document",
    framework: "",
    control_ids: [],
    description: "",
    period_covered: ""
  });
  const [controlsForFw, setControlsForFw] = useState([]);
  const [loadingControls, setLoadingControls] = useState(false);
  const [controlSearch, setControlSearch] = useState("");

  // Load controls whenever the chosen framework changes
  useEffect(() => {
    if (!newEvidence.framework) { setControlsForFw([]); return; }
    let aborted = false;
    setLoadingControls(true);
    axios.get(`${API}/controls`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { framework: newEvidence.framework, limit: 500 },
    }).then((res) => {
      if (!aborted) setControlsForFw(res.data || []);
    }).catch(() => {
      if (!aborted) setControlsForFw([]);
    }).finally(() => {
      if (!aborted) setLoadingControls(false);
    });
    return () => { aborted = true; };
  }, [newEvidence.framework, token]);

  const evidenceTypes = [
    { value: "screenshot", labelKey: "evid.type.screenshot", icon: FileImage },
    { value: "document", labelKey: "evid.type.document", icon: FileText },
    { value: "log", labelKey: "evid.type.log", icon: FileCode },
    { value: "config", labelKey: "evid.type.config", icon: FileCode },
    { value: "certificate", labelKey: "evid.type.certificate", icon: Shield },
    { value: "contract", labelKey: "evid.type.contract", icon: FileText },
    { value: "interview", labelKey: "evid.type.interview", icon: FileText },
    { value: "incident", labelKey: "evid.type.incident", icon: Shield },
    { value: "threat_report", labelKey: "evid.type.threat_report", icon: Shield }
  ];

  const reviewStatuses = [
    { value: "pending", labelKey: "evid.status.pending", color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" },
    { value: "reviewed", labelKey: "evid.status.reviewed", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
    { value: "approved", labelKey: "evid.status.approved", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
    { value: "rejected", labelKey: "evid.status.rejected", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" }
  ];

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [evidenceRes, controlsRes] = await Promise.all([
        axios.get(`${API}/evidence`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/controls?limit=200`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setEvidenceList(evidenceRes.data);
      setControls(controlsRes.data);
    } catch (error) {
      console.error("Failed to fetch evidence:", error);
    } finally {
      setLoading(false);
    }
  };

  const createEvidence = async () => {
    if (!newEvidence.title) {
      toast.error("Please enter an evidence title");
      return;
    }

    try {
      const response = await axios.post(`${API}/evidence`, newEvidence, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Evidence created");
      setShowAddEvidence(false);
      setNewEvidence({
        title: "",
        evidence_type: "document",
        framework: "",
        control_ids: [],
        description: "",
        period_covered: ""
      });
      fetchData();
      return response.data;
    } catch (error) {
      toast.error("Failed to create evidence");
      return null;
    }
  };

  const uploadFile = async (evidenceId, file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      await axios.post(`${API}/evidence/${evidenceId}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success("File uploaded");
      fetchData();
    } catch (error) {
      toast.error("Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  const updateReviewStatus = async (evidenceId, status) => {
    try {
      await axios.put(`${API}/evidence/${evidenceId}/review?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Status updated to ${status}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const deleteEvidence = async (evidenceId) => {
    try {
      await axios.delete(`${API}/evidence/${evidenceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Evidence deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete evidence");
    }
  };

  const getTypeIcon = (type) => {
    const config = evidenceTypes.find(t => t.value === type);
    return config?.icon || FileText;
  };

  const getStatusBadge = (status) => {
    return reviewStatuses.find(s => s.value === status)?.color || "";
  };

  const filteredEvidence = evidenceList.filter(e => {
    const matchesSearch = e.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = selectedType === "all" || e.evidence_type === selectedType;
    const matchesStatus = selectedStatus === "all" || e.review_status === selectedStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const evidenceByType = evidenceTypes.map(type => ({
    ...type,
    count: evidenceList.filter(e => e.evidence_type === type.value).length
  }));

  if (loading) {
    return <TableSkeleton rows={6} columns={5} label="Loading evidence" />;
  }

  return (
    <div className="p-6 space-y-6" data-testid="evidence-page">
      <PageHeader
        title={t("evid.title")}
        description={t("evid.subtitle")}
        actions={
          <Button onClick={() => setShowAddEvidence(true)} data-testid="add-evidence-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t("evid.new")}
          </Button>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                <FileText className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{evidenceList.length}</p>
                <p className="text-xs text-muted-foreground">{t("evid.total")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">
                  {evidenceList.filter(e => e.review_status === "pending").length}
                </p>
                <p className="text-xs text-muted-foreground">{t("evid.pending_review")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">
                  {evidenceList.filter(e => e.review_status === "approved").length}
                </p>
                <p className="text-xs text-muted-foreground">{t("evid.approved")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Link2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">
                  {evidenceList.filter(e => e.control_ids?.length > 0).length}
                </p>
                <p className="text-xs text-muted-foreground">{t("evid.linked_controls")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t("evid.search_ph")}
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="evidence-search-input"
          />
        </div>
        <Select value={selectedType} onValueChange={setSelectedType}>
          <SelectTrigger className="w-full md:w-40" data-testid="evidence-type-filter">
            <SelectValue placeholder={t("type")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("evid.all_types")}</SelectItem>
            {evidenceTypes.map(et => (
              <SelectItem key={et.value} value={et.value}>{t(et.labelKey)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
          <SelectTrigger className="w-full md:w-40" data-testid="evidence-status-filter">
            <SelectValue placeholder={t("status")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("evid.all_statuses")}</SelectItem>
            {reviewStatuses.map(s => (
              <SelectItem key={s.value} value={s.value}>{t(s.labelKey)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Evidence List */}
      <ScrollArea className="h-[calc(100vh-28rem)]">
        <div className="space-y-3 pr-4">
          <AnimatePresence mode="popLayout">
            {filteredEvidence.map((item, index) => {
              const TypeIcon = getTypeIcon(item.evidence_type);
              return (
                <motion.div
                  key={item.evidence_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2, delay: index * 0.02 }}
                >
                  <Card className="border-border hover:border-primary/50 transition-colors" data-testid={`evidence-card-${item.evidence_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                            <TypeIcon className="w-5 h-5 text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <Badge variant="secondary" className="capitalize text-xs">
                                {t(evidenceTypes.find(et => et.value === item.evidence_type)?.labelKey || "evid.type.document")}
                              </Badge>
                              <Badge variant="outline" className={getStatusBadge(item.review_status)}>
                                {t(reviewStatuses.find(s => s.value === item.review_status)?.labelKey || "evid.status.pending")}
                              </Badge>
                              {item.framework && (
                                <Badge variant="outline" className="text-xs">
                                  <Shield className="w-3 h-3 mr-1" />
                                  {FW_SHORT[item.framework] || item.framework}
                                </Badge>
                              )}
                              {item.file_name && (
                                <Badge variant="outline" className="text-xs">
                                  <FileText className="w-3 h-3 mr-1" />
                                  File attached
                                </Badge>
                              )}
                            </div>
                            <h3 className="font-semibold mb-1 truncate">{item.title}</h3>
                            {item.description && (
                              <p className="text-sm text-muted-foreground line-clamp-1 mb-2">
                                {item.description}
                              </p>
                            )}
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              {item.control_ids?.length > 0 && (
                                <span>{item.control_ids.length} {t("evid.linked_count")}</span>
                              )}
                              {item.period_covered && (
                                <span>{t("evid.period_label_detail")}: {item.period_covered}</span>
                              )}
                              <span>{t("evid.added")}: {new Date(item.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Select onValueChange={(v) => updateReviewStatus(item.evidence_id, v)}>
                            <SelectTrigger className="w-32 h-8 text-xs">
                              <SelectValue placeholder={t("evid.update_status")} />
                            </SelectTrigger>
                            <SelectContent>
                              {reviewStatuses.map(s => (
                                <SelectItem key={s.value} value={s.value}>{t(s.labelKey)}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {!item.file_id && (
                            <label>
                              <input
                                type="file"
                                className="hidden"
                                onChange={(e) => e.target.files?.[0] && uploadFile(item.evidence_id, e.target.files[0])}
                                disabled={uploading}
                              />
                              <Button variant="ghost" size="icon" asChild disabled={uploading}>
                                <span>
                                  <Upload className="w-4 h-4" />
                                </span>
                              </Button>
                            </label>
                          )}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>{t("evid.delete_title")}</AlertDialogTitle>
                                <AlertDialogDescription>
                                  {t("evid.delete_desc")}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deleteEvidence(item.evidence_id)}>
                                  {t("delete")}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>

          {filteredEvidence.length === 0 && (
            <EmptyState
              icon={FileText}
              title={t("evid.no_evidence_title")}
              description={t("evid.no_evidence_desc")}
              action={
                <Button onClick={() => setShowAddEvidence(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t("evid.add_first")}
                </Button>
              }
            />
          )}
        </div>
      </ScrollArea>

      {/* Add Evidence Sheet */}
      <Sheet open={showAddEvidence} onOpenChange={setShowAddEvidence}>
        <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col gap-0 p-0">
          <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <SheetTitle>{t("evid.add_sheet_title")}</SheetTitle>
            <SheetDescription>{t("evid.add_sheet_desc")}</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="evidence-title">{t("evid.title_label")} *</Label>
              <Input
                id="evidence-title"
                value={newEvidence.title}
                onChange={(e) => setNewEvidence({ ...newEvidence, title: e.target.value })}
                placeholder={t("evid.ph_title")}
                data-testid="evidence-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t("evid.type_label")}</Label>
              <Select
                value={newEvidence.evidence_type}
                onValueChange={(v) => setNewEvidence({ ...newEvidence, evidence_type: v })}
              >
                <SelectTrigger data-testid="evidence-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {evidenceTypes.map(et => (
                    <SelectItem key={et.value} value={et.value}>{t(et.labelKey)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("evid.period_label")}</Label>
              <Input
                value={newEvidence.period_covered}
                onChange={(e) => setNewEvidence({ ...newEvidence, period_covered: e.target.value })}
                placeholder={t("evid.ph_period")}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("evid.desc_label")}</Label>
              <Textarea
                value={newEvidence.description}
                onChange={(e) => setNewEvidence({ ...newEvidence, description: e.target.value })}
                placeholder={t("description")}
                data-testid="evidence-desc-input"
              />
            </div>

            {/* Framework + controls linkage */}
            <div className="space-y-2">
              <Label>{t("evid.framework_label")}</Label>
              <Select
                value={newEvidence.framework}
                onValueChange={(v) => setNewEvidence({ ...newEvidence, framework: v, control_ids: [] })}
              >
                <SelectTrigger data-testid="evidence-framework-select">
                  <SelectValue placeholder="Selecciona un marco para enlazar controles" />
                </SelectTrigger>
                <SelectContent>
                  {orgFrameworks.map((f) => (
                    <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                La evidencia se enlazará a los controles del marco seleccionado para trazabilidad en SoA, Análisis de Brecha y Reportes.
              </p>
            </div>

            {newEvidence.framework && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Controles enlazados</Label>
                  <span className="text-xs text-muted-foreground">
                    {newEvidence.control_ids.length} seleccionados
                  </span>
                </div>
                <Input
                  placeholder="Buscar control..."
                  value={controlSearch}
                  onChange={(e) => setControlSearch(e.target.value)}
                  className="h-9"
                />
                <div className="border border-border rounded-md max-h-64 overflow-y-auto">
                  {loadingControls ? (
                    <p className="p-3 text-xs text-muted-foreground">Cargando controles...</p>
                  ) : controlsForFw.length === 0 ? (
                    <p className="p-3 text-xs text-muted-foreground">Sin controles para este marco.</p>
                  ) : (
                    controlsForFw
                      .filter((c) => {
                        if (!controlSearch) return true;
                        const q = controlSearch.toLowerCase();
                        return (
                          c.control_number?.toLowerCase().includes(q) ||
                          c.title?.toLowerCase().includes(q)
                        );
                      })
                      .map((c) => {
                        const cn = c.control_number;
                        const checked = newEvidence.control_ids.includes(cn);
                        return (
                          <label
                            key={c.control_id || cn}
                            className="flex items-start gap-2 p-2 text-sm hover:bg-muted/40 cursor-pointer border-b border-border last:border-b-0"
                          >
                            <input
                              type="checkbox"
                              className="mt-0.5"
                              checked={checked}
                              onChange={(e) => {
                                const next = e.target.checked
                                  ? [...newEvidence.control_ids, cn]
                                  : newEvidence.control_ids.filter((x) => x !== cn);
                                setNewEvidence({ ...newEvidence, control_ids: next });
                              }}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="font-mono text-[10px]">{cn}</Badge>
                                <span className="text-sm truncate">{c.title}</span>
                              </div>
                              {c.category && (
                                <p className="text-[11px] text-muted-foreground truncate mt-0.5">{c.category}</p>
                              )}
                            </div>
                          </label>
                        );
                      })
                  )}
                </div>
              </div>
            )}
          </div>
          <SheetFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={() => setShowAddEvidence(false)}>{t("cancel")}</Button>
            <Button onClick={createEvidence} data-testid="save-evidence-btn">{t("evid.create_btn")}</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Evidence;
